import * as zod from 'zod';

import { getIntegrationTypes } from '@sb/integrations/loaders/steps';

import AddOffsetStepArguments from './AddOffset/Arguments';
import AnticipatePayloadStepArguments from './AnticipatePayload/Arguments';
import AntigravityStepArguments from './Antigravity/Arguments';
import ArcStepArguments from './Arc/Arguments';
import CallSubroutineStepArguments from './CallSubroutine/Arguments';
import CaptureStepArguments from './Capture/Arguments';
import ClassifyStepArguments from './Classify/Arguments';
import CodeBlockStepArguments from './CodeBlock/Arguments';
import CreateDemonstrationStepArguments from './CreateDemonstration/Arguments';
import CreateRecordingStepArguments from './CreateRecording/Arguments';
import ElseStepArguments from './Else/Arguments';
import ElseIfStepArguments from './ElseIf/Arguments';
import ExternalControlStepArguments from './ExternalControl/Arguments';
import IfStepArguments from './If/Arguments';
import LocateStepArguments from './Locate/Arguments';
import LoopStepArguments from './Loop/Arguments';
import LoopControlStepArguments from './LoopControl/Arguments';
import MoveArmToStepArguments from './MoveArmTo/Arguments';
import MoveArmToV2StepArguments from './MoveArmToV2/Arguments';
import { NetworkRequestArguments } from './NetworkRequest/Arguments';
import OperateEquipmentStepArguments from './OperateEquipment/Arguments';
import PressButtonStepArguments from './PressButton/Arguments';
import PushModeStepArguments from './PushMode/Arguments';
import RunDemonstrationArguments from './RunDemonstration/Arguments';
import RunInBackgroundStepArguments from './RunInBackground/Arguments';
import RunLocateSkillStepArguments from './RunLocateSkill/Arguments';
import RunSkillArguments from './RunSkill/Arguments';
import SamplePositioningApproachStepArguments from './SamplePositioningApproach/Arguments';
import SetEnvironmentVariableArguments from './SetEnvironmentVariable/Arguments';
import SetIOStepArguments from './SetIO/Arguments';
import SuppressedStepArguments from './Suppressed/Arguments';
import TeleopStepArguments from './Teleop/Arguments';
import TriggerFaultStepArguments from './TriggerFault/Arguments';
import UpdateVisualizationStepArguments from './UpdateVisualization/Arguments';
import WaitStepArguments from './Wait/Arguments';
import WaitForConfirmationStepArguments from './WaitForConfirmation/Arguments';
import WaypointStepArguments from './Waypoint/Arguments';

// Shared values for all step configurations.
// Includes steps, a recursive type. Zod cannot perfectly infer recursive types,
// so we need to include type hints for this part of the configuration.
export type BaseRoutineStepConfiguration = {
  id: string;
  description?: string;
  substepsMinimized?: boolean;
  suppressed?: boolean;
  // eslint-disable-next-line no-use-before-define
  steps?: [RoutineStepConfiguration, ...RoutineStepConfiguration[]];
};

export const BaseRoutineStepConfiguration: zod.ZodSchema<BaseRoutineStepConfiguration> =
  zod.lazy(() =>
    zod.object({
      id: zod.string(),
      description: zod.string().optional(),
      suppressed: zod.boolean().optional(),
      // eslint-disable-next-line no-use-before-define
      steps: zod.array(RoutineStepConfiguration).nonempty().optional(),
    }),
  );

export const TaggedStepConfigUnion = zod.discriminatedUnion('stepKind', [
  zod.object({
    stepKind: zod.literal('AnticipatePayload'),
    args: AnticipatePayloadStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('Antigravity'),
    args: AntigravityStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('AddOffset'),
    args: AddOffsetStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('Arc'),
    args: ArcStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('CallSubroutine'),
    args: CallSubroutineStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('Capture'),
    args: CaptureStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('Classify'),
    args: ClassifyStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('Locate'),
    args: LocateStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('Else'),
    args: ElseStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('ElseIf'),
    args: ElseIfStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('ExternalControl'),
    args: ExternalControlStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('If'),
    args: IfStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('Loop'),
    args: LoopStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('LoopControl'),
    args: LoopControlStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('MoveArmTo'),
    args: MoveArmToStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('MoveArmToV2'),
    args: MoveArmToV2StepArguments,
  }),
  zod.object({
    stepKind: zod.literal('NetworkRequest'),
    args: NetworkRequestArguments,
  }),
  zod.object({
    stepKind: zod.literal('OperateEquipment'),
    args: OperateEquipmentStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('PressButton'),
    args: PressButtonStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('PushMode'),
    args: PushModeStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('SamplePositioningApproach'),
    args: SamplePositioningApproachStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('RunInBackground'),
    args: RunInBackgroundStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('RunLocateSkill'),
    args: RunLocateSkillStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('RunLearnedModel'),
    args: RunSkillArguments,
  }),
  zod.object({
    stepKind: zod.literal('RunDemonstration'),
    args: RunDemonstrationArguments,
  }),
  zod.object({
    stepKind: zod.literal('SetEnvironmentVariable'),
    args: SetEnvironmentVariableArguments,
  }),
  zod.object({
    stepKind: zod.literal('SetIO'),
    args: SetIOStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('Suppressed'),
    args: SuppressedStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('Teleop'),
    args: TeleopStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('WaitForConfirmation'),
    args: WaitForConfirmationStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('Wait'),
    args: WaitStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('Waypoint'),
    args: WaypointStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('CodeBlock'),
    args: CodeBlockStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('CreateRecording'),
    args: CreateRecordingStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('CreateDemonstration'),
    args: CreateDemonstrationStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('TriggerFault'),
    args: TriggerFaultStepArguments,
  }),
  zod.object({
    stepKind: zod.literal('UpdateVisualization'),
    args: UpdateVisualizationStepArguments,
  }),
  ...getIntegrationTypes().map(({ stepKind, Arguments }) =>
    zod.object({
      stepKind: zod.literal(stepKind),
      args: Arguments,
    }),
  ),
]);

type TaggedStepConfigUnion = zod.infer<typeof TaggedStepConfigUnion>;

export type RoutineStepConfiguration = TaggedStepConfigUnion &
  BaseRoutineStepConfiguration;

export const RoutineStepConfiguration: zod.ZodSchema<
  RoutineStepConfiguration,
  zod.ZodTypeDef,
  any
> = zod.lazy(() => BaseRoutineStepConfiguration.and(TaggedStepConfigUnion));
